import { Link, useNavigate, useRouter } from '@tanstack/react-router'
import {
  ConnectDialog,
  type ConnectDialogTexts,
} from '@workspace/seedforge-features/profile/components/connect-dialog'
import { PROFILE_VIEW_SECTION } from '@workspace/seedforge-shared'
import { useToast } from '@workspace/ui/components/toast'
import { ArrowLeft, Ellipsis, FileText, Mail } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { useCurrentUserQuery } from '../../../../hooks/api'
import { trackEvent } from '../../../../lib/gtag'
import { SharedProfilePage } from '../../../shared-profile/shared-profile-page'
import { copy } from '../../copy/investor-copy'
import {
  useMarkWatchdogSeenOnOpen,
  useStartupRelationship,
} from '../../hooks/use-investor-assistant'
import { ActionMenu } from '../action-menu'
import { formatShortDate } from '../following/dates'
import { FollowControl } from './follow-control'
import { RelationshipCards, useRelationshipActions } from './relationship-cards'
import type { RelationshipUpdate } from './types'

type Startup = { id: string; name: string }

const SOURCE_SECTION: Partial<Record<RelationshipUpdate['kind'], string>> = {
  deck: PROFILE_VIEW_SECTION.DECK,
  document: PROFILE_VIEW_SECTION.DOCUMENTS,
  update: PROFILE_VIEW_SECTION.DOCUMENTS,
}

function introTexts(startup: string): ConnectDialogTexts {
  const vars = { startup }
  return {
    title: copy('intro.title', vars),
    description: copy('intro.desc'),
    label: copy('intro.label'),
    tip: copy('intro.tip'),
    cancel: copy('intro.cancel'),
    send: copy('intro.send'),
  }
}

function StartupPageFrame({
  startup,
  profile,
  intro,
  token,
  from,
}: {
  startup: Startup
  profile: (titleActions: ReactNode) => ReactNode
  intro: {
    send: (message: string) => void
    pending: boolean
    status: 'idle' | 'pending' | 'success' | 'error'
  }
  token: string
  from: 'search' | undefined
}) {
  const { investor } = useInvestorContext()
  const router = useRouter()
  const navigate = useNavigate()
  const query = useStartupRelationship(startup.id)
  const data = query.data
  useMarkWatchdogSeenOnOpen(startup.id)
  const profileRef = useRef<HTMLDivElement>(null)
  const [introOpen, setIntroOpen] = useState(false)
  const toast = useToast()
  const introStatus = intro.status
  useEffect(() => {
    if (introStatus === 'success') toast(copy('toast.intro.sent', { startup: startup.name }))
    if (introStatus === 'error') toast(copy('toast.intro.error'))
  }, [introStatus, toast, startup.name])
  const scrollToSection = (key: string) => {
    const section = profileRef.current?.querySelector(`[data-section="${key}"]`)
    ;(section ?? profileRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  // The update whose text is the one given (its first words are enough); the documents when none is.
  const scrollToUpdate = (text: string) => {
    const head = text.slice(0, 40)
    const items = profileRef.current?.querySelectorAll<HTMLElement>('[data-update]') ?? []
    const match = [...items].find((el) => el.dataset.update?.includes(head))
    if (match) match.scrollIntoView({ behavior: 'smooth', block: 'center' })
    else scrollToSection(PROFILE_VIEW_SECTION.DOCUMENTS)
  }
  const { actions, dialogs, note } = useRelationshipActions({
    startupId: startup.id,
    data,
    // The profile is the page: an update's source is the page itself.
    openProfile: () => profileRef.current?.scrollIntoView({ behavior: 'smooth' }),
    onBack: () => window.history.back(),
    shareToken: token,
    openSource: (update) => {
      // A founder's update is its own item in the profile; a deck or a document is its section.
      if (update.kind === 'update') scrollToUpdate(update.title)
      else scrollToSection(SOURCE_SECTION[update.kind] ?? PROFILE_VIEW_SECTION.HEADER)
    },
    openUpdate: scrollToUpdate,
  })
  const updated = data?.startup.updatedAt
  useEffect(() => {
    trackEvent('startup_viewed', {
      startup_id: startup.id,
      surface: 'page',
      source: from ?? 'following',
    })
  }, [startup.id, from])

  const titleActions = (
    <>
      <div className="flex items-center gap-2">
        <FollowControl
          startupId={startup.id}
          startupName={startup.name}
          shareToken={token}
          quiet={note.editing}
        />
        <ActionMenu
          label={copy('panel.aria.more', { startup: startup.name })}
          className="w-72"
          actions={[
            {
              key: 'intro',
              icon: Mail,
              title: copy('menu.getIntro'),
              hint: copy('menu.getIntro.hint'),
              onSelect: () => setIntroOpen(true),
            },
            {
              key: 'note',
              icon: FileText,
              title: copy('menu.writeNote'),
              hint: copy('menu.writeNote.hint'),
              onSelect: actions.onWriteNote,
            },
          ]}
          trigger={
            <button
              type="button"
              aria-label={copy('panel.aria.more', { startup: startup.name })}
              className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-input text-foreground hover:bg-hover"
            >
              <Ellipsis className="size-4" />
            </button>
          }
        />
      </div>
      {updated && (
        <p className="text-xs text-muted-foreground">
          {copy('page.updated', { date: formatShortDate(updated) })}
        </p>
      )}
    </>
  )

  return (
    <div className="@container mx-auto max-w-7xl space-y-4 py-2 pb-10">
      {from === 'search' ? (
        // Back returns to the page the investor came from, with its tab and open startup, as the browser remembers it.
        <button
          type="button"
          onClick={() =>
            router.history.canGoBack()
              ? router.history.back()
              : navigate({ to: '/app/investor/$id/search', params: { id: investor.id } })
          }
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          {copy('page.backSearch')}
        </button>
      ) : (
        <Link
          to="/app/investor/$id/follows"
          params={{ id: investor.id }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          {copy('page.back')}
        </Link>
      )}
      <div className="grid gap-6 @min-[900px]:grid-cols-[minmax(0,1fr)_400px]">
        <div ref={profileRef} className="min-w-0">
          {profile(titleActions)}
        </div>
        <aside className="space-y-4 pb-24">
          {data && <RelationshipCards data={data} variant="page" actions={actions} note={note} />}
        </aside>
      </div>
      {dialogs}
      <ConnectDialog
        startupName={startup.name}
        open={introOpen}
        onOpenChange={setIntroOpen}
        onConnect={(message) => {
          intro.send(message)
          setIntroOpen(false)
        }}
        isPending={intro.pending}
        texts={introTexts(startup.name)}
      />
    </div>
  )
}

/** A startup's shared profile inside the investor's app, with the follow and the cards around it. */
export function StartupPage({ token, from }: { token: string; from: 'search' | undefined }) {
  const { data: me } = useCurrentUserQuery()
  return (
    <SharedProfilePage
      token={token}
      app={{
        viewerEmail: me?.email ?? null,
        render: (startup, profile, intro) => (
          <StartupPageFrame
            startup={startup}
            profile={profile}
            intro={intro}
            token={token}
            from={from}
          />
        ),
      }}
    />
  )
}
